import {
  isStandardParameter,
  STANDARD_PARAMETER_KEYS,
  STANDARD_PARAMETERS,
  type StandardParameterReference,
  standardParameterNamed,
  unitForCode,
} from "@repo/domain";
import { z } from "zod";

const choiceOptionSchema = z.object({ id: z.string(), label: z.string().min(1, "Option label is required") });

export const makeClinicalValueFormSchema = (requireBounds = true) =>
  z
    .object({
      kind: z.enum(["Quantity", "Integer", "Choice", "Boolean", "Text"]),
      // ONE field, holding the catalogued unit's code. The display is resolved
      // from UNIT_CATALOG at the submit boundary rather than stored beside it:
      // a unit is PICKED, never typed, so there is no second spelling to keep in
      // step. Empty means unitless.
      unitCode: z.string(),
      min: z.number().nullable(),
      max: z.number().nullable(),
      step: z.number().positive().nullable(),
      maxLength: z.number().int().positive().nullable(),
      options: z.array(choiceOptionSchema),
      cardinality: z.enum(["Single", "Multiple"]),
      allowFreeText: z.boolean(),
    })
    .superRefine((value, context) => {
      if (value.kind === "Quantity" || value.kind === "Integer") {
        for (const field of ["min", "max"] as const) {
          if (requireBounds && value[field] == null) {
            context.addIssue({
              code: "custom",
              message: `${field === "min" ? "Minimum" : "Maximum"} is required`,
              path: [field],
            });
          }
        }
        if (requireBounds && value.kind === "Quantity" && value.step == null) {
          context.addIssue({ code: "custom", message: "Step is required", path: ["step"] });
        }
        if (value.kind === "Integer" && value.step != null && !Number.isInteger(value.step)) {
          context.addIssue({ code: "custom", message: "Step must be a whole number", path: ["step"] });
        }
      }
      if (value.kind === "Choice" && value.options.length === 0) {
        context.addIssue({ code: "custom", message: "Choice values require at least one option", path: ["options"] });
      }
    });

export const clinicalValueFormSchema = makeClinicalValueFormSchema();

export const clinicalDefinitionParameterFormSchema = z.object({
  key: z.string().optional(),
  id: z.string(),
  label: z.string().min(1, "Parameter label is required"),
  value: clinicalValueFormSchema,
});

export const clinicalDefinitionComponentFormSchema = z.object({
  key: z.string().optional(),
  id: z.string(),
  label: z.string().min(1, "Component label is required"),
  value: clinicalValueFormSchema,
});

export const clinicalDefinitionObservationFormSchema = z.object({
  key: z.string().optional(),
  id: z.string(),
  label: z.string().min(1, "Observation label is required"),
  repeatable: z.boolean(),
  noteLabel: z.string(),
  components: z.array(clinicalDefinitionComponentFormSchema).min(1, "An observation requires at least one component"),
});

export const clinicalDefinitionRequirementsFormSchema = z.object({
  devices: z.array(z.object({ id: z.string(), definition: z.string().url("Enter a canonical device URL") })),
  location: z.string(),
});

const standardParameterReferenceFormSchema = z.object({
  kind: z.literal("standard"),
  key: z.enum(STANDARD_PARAMETER_KEYS),
});

/** Shared authoring fields only; resource-specific cardinality and scheduling rules extend this schema. */
export const clinicalDefinitionFormSchema = z.object({
  procedureNoteLabel: z.string(),
  observations: z.array(clinicalDefinitionObservationFormSchema),
  parameters: z
    .array(z.union([clinicalDefinitionParameterFormSchema, standardParameterReferenceFormSchema]))
    .superRefine((parameters, context) => {
      const enabled = new Set(parameters.filter(isStandardParameter).map((parameter) => parameter.key));
      parameters.forEach((parameter, index) => {
        if (isStandardParameter(parameter)) return;
        const named = standardParameterNamed(parameter.label);
        if (named && enabled.has(named))
          context.addIssue({
            code: "custom",
            path: [index, "label"],
            message: `"${parameter.label.trim()}" is the standard ${STANDARD_PARAMETERS[named].label} parameter. Rename it or turn ${STANDARD_PARAMETERS[named].label} off.`,
          });
      });
    }),
  requirements: clinicalDefinitionRequirementsFormSchema,
});

export type ClinicalValueFormValues = z.infer<typeof clinicalValueFormSchema>;
type ClinicalDefinitionFields = z.infer<typeof clinicalDefinitionFormSchema>;
export type ClinicalDefinitionFormValues = ClinicalDefinitionFields;

export type ClinicalValueDefinition =
  | { kind: "Quantity"; unit?: { code: string; display: string }; min?: number; max?: number; step?: number }
  | { kind: "Integer"; min?: number; max?: number; step?: number }
  | {
      kind: "Choice";
      options: readonly { id?: string; label: string }[];
      cardinality: "Single" | "Multiple";
      allowFreeText: boolean;
    }
  | { kind: "Boolean" }
  | { kind: "Text"; maxLength?: number };

export interface ClinicalDefinitionFacts {
  procedureNoteLabel?: string;
  observations: readonly {
    id: string;
    label: string;
    repeatable: boolean;
    noteLabel?: string;
    components: readonly { id: string; label: string; value: ClinicalValueDefinition }[];
  }[];
  parameters: readonly ({ id: string; label: string; value: ClinicalValueDefinition } | StandardParameterReference)[];
  deviceRequirements: readonly { id: string; definition: string }[];
  locationRequirement?: string;
}

export type ClinicalValueInput =
  | { kind: "Quantity"; unit?: { code: string; display: string }; min?: number; max?: number; step?: number }
  | { kind: "Integer"; min?: number; max?: number; step?: number }
  | {
      kind: "Choice";
      options: { id?: string; label: string }[];
      cardinality: "Single" | "Multiple";
      allowFreeText: boolean;
    }
  | { kind: "Boolean" }
  | { kind: "Text"; maxLength?: number };

export interface ClinicalDefinitionContentInput {
  procedureNoteLabel?: string;
  observations: Array<{
    id?: string;
    label: string;
    repeatable: boolean;
    noteLabel?: string;
    components: Array<{ id?: string; label: string; value: ClinicalValueInput }>;
  }>;
  parameters: Array<{ id?: string; label: string; value: ClinicalValueInput } | StandardParameterReference>;
  requirements: { devices: Array<{ id?: string; definition: string }>; location?: string };
}

export interface DefinitionMeta {
  name: string;
  description: string;
}

export const EMPTY_CLINICAL_VALUE: ClinicalValueFormValues = {
  kind: "Text",
  unitCode: "",
  min: null,
  max: null,
  step: null,
  maxLength: null,
  options: [],
  cardinality: "Single",
  allowFreeText: false,
};

export const EMPTY_CLINICAL_PARAMETER: z.infer<typeof clinicalDefinitionParameterFormSchema> = {
  id: "",
  label: "",
  value: EMPTY_CLINICAL_VALUE,
};

export const EMPTY_CLINICAL_COMPONENT: ClinicalDefinitionFormValues["observations"][number]["components"][number] = {
  id: "",
  label: "",
  value: EMPTY_CLINICAL_VALUE,
};

export const EMPTY_CLINICAL_OBSERVATION: ClinicalDefinitionFormValues["observations"][number] = {
  id: "",
  label: "",
  repeatable: false,
  noteLabel: "",
  components: [EMPTY_CLINICAL_COMPONENT],
};

/** Allocate React list keys for unsaved rows. */
export function createClinicalComponent(): ClinicalDefinitionFormValues["observations"][number]["components"][number] {
  return {
    ...structuredClone(EMPTY_CLINICAL_COMPONENT),
    key: `row-${crypto.randomUUID()}`,
  };
}

export function createClinicalObservation(): ClinicalDefinitionFormValues["observations"][number] {
  return {
    ...structuredClone(EMPTY_CLINICAL_OBSERVATION),
    key: `row-${crypto.randomUUID()}`,
    components: [createClinicalComponent()],
  };
}

export const EMPTY_CLINICAL_DEFINITION: ClinicalDefinitionFormValues = {
  procedureNoteLabel: "",
  observations: [],
  parameters: [],
  requirements: { devices: [], location: "" },
};

export function clinicalValueToForm(value: ClinicalValueDefinition): ClinicalValueFormValues {
  return {
    ...EMPTY_CLINICAL_VALUE,
    ...value,
    unitCode: value.kind === "Quantity" ? (value.unit?.code ?? "") : "",
    min: value.kind === "Quantity" || value.kind === "Integer" ? (value.min ?? null) : null,
    max: value.kind === "Quantity" || value.kind === "Integer" ? (value.max ?? null) : null,
    step: value.kind === "Quantity" || value.kind === "Integer" ? (value.step ?? null) : null,
    maxLength: value.kind === "Text" ? (value.maxLength ?? null) : null,
    options:
      value.kind === "Choice" ? value.options.map((option) => ({ id: option.id ?? "", label: option.label })) : [],
    cardinality: value.kind === "Choice" ? value.cardinality : "Single",
    allowFreeText: value.kind === "Choice" ? value.allowFreeText : false,
  };
}

export function clinicalDefinitionToDefaults(
  definition: ClinicalDefinitionFacts | null | undefined,
  emptyDefinition: ClinicalDefinitionFormValues = EMPTY_CLINICAL_DEFINITION,
): ClinicalDefinitionFormValues {
  if (!definition) {
    const defaults = structuredClone(emptyDefinition);
    for (const observation of defaults.observations) {
      observation.key = `row-${crypto.randomUUID()}`;
      for (const component of observation.components) {
        component.key = `row-${crypto.randomUUID()}`;
      }
    }
    for (const parameter of defaults.parameters.filter((p) => !isStandardParameter(p)))
      parameter.key = `row-${crypto.randomUUID()}`;
    return defaults;
  }
  return {
    procedureNoteLabel: definition.procedureNoteLabel ?? "",
    observations: definition.observations.map((observation) => ({
      ...observation,
      noteLabel: observation.noteLabel ?? "",
      components: observation.components.map((component) => ({
        ...component,
        value: clinicalValueToForm(component.value),
      })),
    })),
    parameters: definition.parameters.map((parameter) =>
      isStandardParameter(parameter)
        ? { ...parameter }
        : {
            ...parameter,
            value: clinicalValueToForm(parameter.value),
          },
    ),
    requirements: {
      devices: definition.deviceRequirements.map((requirement) => ({ ...requirement })),
      location: definition.locationRequirement ?? "",
    },
  };
}

export function clinicalValueToInput(value: ClinicalValueFormValues): ClinicalValueInput {
  switch (value.kind) {
    case "Quantity":
      return {
        kind: "Quantity",
        // The code is the identity and the only thing the form holds; an
        // uncatalogued one still reads, standing in as its own display.
        ...(value.unitCode ? { unit: unitForCode(value.unitCode) } : {}),
        ...(value.min === null ? {} : { min: value.min }),
        ...(value.max === null ? {} : { max: value.max }),
        ...(value.step === null ? {} : { step: value.step }),
      };
    case "Integer":
      return {
        kind: "Integer",
        ...(value.min === null ? {} : { min: value.min }),
        ...(value.max === null ? {} : { max: value.max }),
        ...(value.step === null ? {} : { step: value.step }),
      };
    case "Choice":
      return {
        kind: "Choice",
        options: value.options.map((option) => ({ ...(option.id ? { id: option.id } : {}), label: option.label })),
        cardinality: value.cardinality,
        allowFreeText: value.allowFreeText,
      };
    case "Boolean":
      return { kind: "Boolean" };
    case "Text":
      return { kind: "Text", ...(value.maxLength === null ? {} : { maxLength: value.maxLength }) };
  }
}

export function clinicalDefinitionValuesToInput(values: ClinicalDefinitionFormValues): ClinicalDefinitionContentInput {
  return {
    ...(values.procedureNoteLabel ? { procedureNoteLabel: values.procedureNoteLabel } : {}),
    observations: values.observations.map((observation) => ({
      ...(observation.id ? { id: observation.id } : {}),
      label: observation.label,
      repeatable: observation.repeatable,
      ...(observation.noteLabel ? { noteLabel: observation.noteLabel } : {}),
      components: observation.components.map((component) => ({
        ...(component.id ? { id: component.id } : {}),
        label: component.label,
        value: clinicalValueToInput(component.value),
      })),
    })),
    parameters: values.parameters.map((parameter) =>
      isStandardParameter(parameter)
        ? { ...parameter }
        : {
            ...(parameter.id ? { id: parameter.id } : {}),
            label: parameter.label,
            value: clinicalValueToInput(parameter.value),
          },
    ),
    requirements: {
      devices: values.requirements.devices.map((requirement) => ({
        ...(requirement.id ? { id: requirement.id } : {}),
        definition: requirement.definition,
      })),
      ...(values.requirements.location ? { location: values.requirements.location } : {}),
    },
  };
}

/** Draft identity never enters the API payload; persisted member IDs remain domain-owned. */
export function clinicalItemKey(item: { id: string; key?: string } | StandardParameterReference): string {
  return isStandardParameter(item) ? `standard-${item.key}` : (item.key ?? item.id);
}
